import { useEffect, useState } from 'react';
import type { Share, ShareColumn, ShareConfig } from '../../shared/types';
import { COLUMN_LABELS, SHARE_COLUMNS } from '../../shared/types';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { formatFullDate } from '../lib/format';
import { buildPeriod } from '../lib/period';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorBanner, Modal, Spinner } from '../components/ui';

const DEFAULT_COLUMNS: ShareColumn[] = ['work_date', 'project', 'title', 'work_type', 'minutes'];

function shareUrl(id: string): string {
  return `${window.location.origin}/s/${id}`;
}

function describe(config: ShareConfig): string {
  const range =
    config.range.mode === 'rolling'
      ? `last ${config.range.days} days`
      : `${formatFullDate(config.range.from)} – ${formatFullDate(config.range.to)}`;
  const scope = config.projectIds.length ? `${config.projectIds.length} project(s)` : 'all projects';
  return `${scope} · ${range} · ${config.columns.length} columns`;
}

export function ShareLinks() {
  const { projects, weekStartsOn } = useStore();
  const [shares, setShares] = useState<Share[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const thisMonth = buildPeriod('month', new Date(), weekStartsOn);

  const [label, setLabel] = useState('Client view');
  const [columns, setColumns] = useState<ShareColumn[]>(DEFAULT_COLUMNS);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [rangeMode, setRangeMode] = useState<'rolling' | 'fixed'>('rolling');
  const [days, setDays] = useState('30');
  const [from, setFrom] = useState(thisMonth.from);
  const [to, setTo] = useState(thisMonth.to);
  const [showTotals, setShowTotals] = useState(true);
  const [showAmounts, setShowAmounts] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setShares(await api.listShares());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load share links');
      setShares([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Amounts and the amount column travel together: turning money off must drop it.
  useEffect(() => {
    if (!showAmounts) setColumns((prev) => prev.filter((c) => c !== 'amount'));
  }, [showAmounts]);

  const toggleColumn = (column: ShareColumn) =>
    setColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : SHARE_COLUMNS.filter((c) => c === column || prev.includes(c)),
    );

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const config: ShareConfig = {
        projectIds,
        range:
          rangeMode === 'rolling'
            ? { mode: 'rolling', days: Number(days) }
            : { mode: 'fixed', from, to },
        columns,
        showTotals,
        showAmounts,
      };
      const share = await api.createShare(label, config, null);
      await load();
      setCreating(false);
      await copy(share.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the link');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(id));
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard access can be denied; the URL is visible on the card regardless.
    }
  };

  const revoke = async (share: Share) => {
    if (!confirm(`Revoke "${share.label}"? Anyone holding the link will lose access immediately.`)) return;
    try {
      await api.revokeShare(share.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revoke the link');
    }
  };

  const availableColumns = SHARE_COLUMNS.filter((c) => c !== 'amount' || showAmounts);

  return (
    <>
      <PageHeader
        title="Share links"
        subtitle="Read-only views that show only the columns you pick."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            New share link
          </button>
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {shares === null ? (
        <div className="card">
          <Spinner label="Loading links" />
        </div>
      ) : shares.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No share links yet"
            description="Create a link to let a client see selected columns of your timesheet without signing in. Rates and amounts stay hidden unless you allow them."
            action={
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Create a link
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {shares.map((share) => (
            <article
              key={share.id}
              className={`card flex flex-wrap items-center gap-4 px-5 py-4 ${share.revoked ? 'opacity-60' : ''}`}
            >
              <div className="min-w-[16rem] flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-[16px]">{share.label}</h2>
                  {share.revoked ? (
                    <span className="rounded-[var(--radius-pill)] bg-[var(--color-coral-100)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-coral-700)]">
                      Revoked
                    </span>
                  ) : share.config.showAmounts ? (
                    <span className="rounded-[var(--radius-pill)] bg-[var(--color-gold-100)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-gold-600)]">
                      Shows amounts
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-[12px] text-[var(--color-ink-muted)]">{describe(share.config)}</p>
                {!share.revoked && (
                  <code className="mt-2 block truncate rounded-lg bg-[var(--color-paper-sunk)] px-2.5 py-1.5 text-[12px] text-[var(--color-ink-soft)]">
                    {shareUrl(share.id)}
                  </code>
                )}
              </div>

              {!share.revoked && (
                <div className="flex gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => void copy(share.id)}>
                    {copied === share.id ? 'Copied' : 'Copy link'}
                  </button>
                  <a
                    className="btn btn-ghost"
                    href={shareUrl(share.id)}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Open
                  </a>
                  <button type="button" className="btn btn-danger" onClick={() => void revoke(share)}>
                    Revoke
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {creating && (
        <Modal
          title="New share link"
          onClose={() => setCreating(false)}
          wide
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void create()}
                disabled={busy || columns.length === 0}
              >
                {busy ? 'Creating…' : 'Create and copy link'}
              </button>
            </>
          }
        >
          <div className="space-y-5">
            <div>
              <label className="label" htmlFor="sh-label">
                Label
              </label>
              <input
                id="sh-label"
                className="field"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div>
              <span className="label">Projects</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setProjectIds([])}
                  className={`btn ${projectIds.length === 0 ? 'btn-primary' : 'btn-secondary'} !py-1.5`}
                >
                  All projects
                </button>
                {projects.map((p) => {
                  const on = projectIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        setProjectIds((prev) =>
                          prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id],
                        )
                      }
                      className={`btn ${on ? 'btn-primary' : 'btn-secondary'} !py-1.5`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className="label">Date range</span>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  className="field !w-auto"
                  value={rangeMode}
                  onChange={(e) => setRangeMode(e.target.value as 'rolling' | 'fixed')}
                  aria-label="Range mode"
                >
                  <option value="rolling">Rolling window</option>
                  <option value="fixed">Fixed dates</option>
                </select>

                {rangeMode === 'rolling' ? (
                  <label className="flex items-center gap-2 text-[13px]">
                    Last
                    <input
                      className="field nums !w-20 text-right"
                      inputMode="numeric"
                      value={days}
                      onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
                    />
                    days
                  </label>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      className="field !w-auto"
                      value={from}
                      max={to}
                      onChange={(e) => setFrom(e.target.value)}
                      aria-label="From"
                    />
                    <span className="text-[var(--color-ink-muted)]">–</span>
                    <input
                      type="date"
                      className="field !w-auto"
                      value={to}
                      min={from}
                      onChange={(e) => setTo(e.target.value)}
                      aria-label="To"
                    />
                  </div>
                )}
              </div>
              {rangeMode === 'rolling' && (
                <p className="mt-1.5 text-[12px] text-[var(--color-ink-muted)]">
                  A rolling window always shows the most recent days, so the link stays current.
                </p>
              )}
            </div>

            <div>
              <span className="label">Visible columns</span>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {availableColumns.map((column) => (
                  <label
                    key={column}
                    className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-[13px] transition-colors ${
                      columns.includes(column)
                        ? 'border-[var(--color-blue-400)] bg-[var(--color-blue-50)]'
                        : 'border-[var(--color-line)] bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="accent-[var(--color-blue-600)]"
                      checked={columns.includes(column)}
                      onChange={() => toggleColumn(column)}
                    />
                    {COLUMN_LABELS[column]}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
              <label className="flex cursor-pointer items-center gap-2.5 text-[13px]">
                <input
                  type="checkbox"
                  className="accent-[var(--color-blue-600)]"
                  checked={showTotals}
                  onChange={(e) => setShowTotals(e.target.checked)}
                />
                Show a totals row
              </label>
              <label className="flex cursor-pointer items-center gap-2.5 text-[13px]">
                <input
                  type="checkbox"
                  className="accent-[var(--color-coral-500)]"
                  checked={showAmounts}
                  onChange={(e) => setShowAmounts(e.target.checked)}
                />
                Show money
                <span className="text-[12px] text-[var(--color-ink-muted)]">
                  — off by default; leaves rates and amounts out of the page entirely
                </span>
              </label>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
